"use client";

import { FC, Fragment, useEffect, useState, useMemo, useCallback, useRef, useLayoutEffect } from "react";
import { useCompetitionContext } from "../../providers/CompetitionContext";
import Image from "next/image";
import { useParams } from "next/navigation";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import moment from "moment";
import { Match, Week } from "@repo/types";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { signIn } from "next-auth/react";
import { track } from '@vercel/analytics/react';

import MatchCard, { EMPTY_PICK, PickValue } from "../../components/MatchCard";
import LeaguePromoCard from "../../components/LeaguePromoCard";
import PointsCard from "../../components/PointsCard";
import { WeekPrizeWidget } from "../../components/WeekPrizeWidget";
import { hasWeeklyPrizeForCompetitionWeek } from "@/lib/weekPrizes";
import { useTranslation } from "../../hooks/useTranslation";
import { PredictionsPageSkeleton } from "../../components/PredictionsSkeleton";

const PREDICTIONS_LOGIN_HERO_SRC =
  "https://ocnc-fantasy.s3.eu-west-1.amazonaws.com/files/prono-rtbf-login.png";

/** Same as prono-dpg-ui: first matchday through next calendar day at this hour (exclusive end). */
const NEXT_UP_CUTOFF_HOUR = 12;

interface PredictionsProps {
  weekId: number;
  user: {
    id?: string;
    favClub?: {
      id: string | number;
      name: string;
    };
    email?: string;
    username?: string;
    token?: any;
  } | null;
}

interface WeekSwitchProps {
  competitionData: any;
}

type ApiResult = {
  round: number | null;
  winnerId: number | string | null;
  outcome: string | null;
};

type ApiPredictionItem = {
  PK: string;
  SK: string;
  userId: number;
  matchId: number;
  weekId: number;
  result: ApiResult;
  predictionTimestamp: string;
  matchDeadline: string;
  booster: boolean; // <— top-level on API items
  // ...other attrs omitted
};

type PredictionsResponse = {
  success: boolean;
  competitionFeed: string;
  environment: string;
  predictions: ApiPredictionItem[];
  count: number;
};

type ClubMatchesResponse = {
  matches: Match[];
};

type RankingEntryResponse = {
  userId: string;
  rank: number;
  score: number;
  userName?: string;
  correctPredictions?: number;
  totalPredictions?: number;
  accuracy?: number;
};

type UserRanksResponse = {
  generalRank: RankingEntryResponse | null;
  weekRank: RankingEntryResponse | null;
  streakRank?: RankingEntryResponse | null;
  qmusicRank?: RankingEntryResponse | null;
  expertsRank?: RankingEntryResponse | null;
};

// ---------------------- API functions ----------------------
const fetchPredictions = async (
  competitionFeed: string,
  seasonId: string | number,
  environment: string = "prd"
): Promise<PredictionsResponse> => {
  console.log('[fetchPredictions] Fetching predictions:', { competitionFeed, seasonId, environment });
  
  const response = await fetch(
    `/api/predictions?competitionFeed=${competitionFeed}&seasonId=${seasonId}&environment=${environment}`,
    {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    }
  );

  console.log('[fetchPredictions] Response:', {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('[fetchPredictions] Error response:', errorText);
    throw new Error("Failed to fetch predictions");
  }

  const data = await response.json();
  console.log('[fetchPredictions] Success:', {
    predictionsCount: data.predictions?.length ?? 0,
    competitionFeed: data.competitionFeed
  });
  
  return data;
};

const fetchClubMatches = async (
  seasonId: number | string,
  clubId: number | string
): Promise<ClubMatchesResponse> => {
  const params = new URLSearchParams({
    seasonId: String(seasonId),
    clubId: String(clubId),
  });

  const response = await fetch(`/api/matches?${params.toString()}`, {
    headers: { "Content-Type": "application/json" },
    credentials: "include",
  });

  console.log('fetchClubMatches response', response);

  if (!response.ok) {
    const errorText = await response.text();
    console.error("[fetchClubMatches] Error response:", errorText);
    throw new Error("Failed to fetch favorite club matches");
  }

  return response.json();
};

const fetchUserRanks = async (
  competitionFeed: string,
  seasonId: string | number,
  userId: string,
  weekId?: number
): Promise<UserRanksResponse> => {
  const params = new URLSearchParams({
    competitionFeed,
    seasonId: String(seasonId),
    userId,
  });

  if (typeof weekId === "number" && weekId >= 0) {
    params.set("weekId", String(weekId));
  }

  const response = await fetch(`/api/rankings/user?${params.toString()}`, {
    headers: { "Content-Type": "application/json" },
    credentials: "include",
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error("[fetchUserRanks] Error response:", errorText);
    throw new Error("Failed to fetch user ranks");
  }

  const data = (await response.json()) as UserRanksResponse;
  return data;
};

const savePrediction = async (data: {
  competitionFeed: string;
  seasonId: string | number;
  environment?: string;
  matchId: number;
  result: ApiResult; // NO booster in result
  booster?: boolean; // booster is top-level
  matchDeadline: string;
}) => {
  console.log('[savePrediction] Saving prediction:', {
    matchId: data.matchId,
    winnerId: data.result?.winnerId,
    outcome: data.result?.outcome,
    round: data.result?.round,
    booster: data.booster,
    competitionFeed: data.competitionFeed,
    seasonId: data.seasonId
  });
  
  const response = await fetch("/api/predictions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(data),
  });

  console.log('[savePrediction] Response:', {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[savePrediction] Error:', error);
    throw new Error(error.message || "Failed to save prediction");
  }

  const result = await response.json();
  console.log('[savePrediction] Success:', result);
  return result;
};

// ---------------------- Date-based Week Helpers ----------------------
// Week 1 starts on the date in NEXT_PUBLIC_WEEK_ONE_START (Friday-Thursday weeks).
// Feature is hard-disabled when the env var is missing/invalid.
const WEEK_ONE_START_RAW = process.env.NEXT_PUBLIC_WEEK_ONE_START;
const WEEK_ONE_START = WEEK_ONE_START_RAW ? moment(WEEK_ONE_START_RAW) : null;
const WEEK_ONE_START_ENABLED = !!WEEK_ONE_START && WEEK_ONE_START.isValid();

const getMatchUtcMoment = (date: unknown): moment.Moment | null => {
  if (!date) return null;
  const matchMoment = moment.utc(date as moment.MomentInput);
  return matchMoment.isValid() ? matchMoment : null;
};

const getMatchLocalMoment = (date: unknown): moment.Moment | null =>
  getMatchUtcMoment(date)?.local() ?? null;

const getMatchTimeValue = (date: unknown): number =>
  getMatchUtcMoment(date)?.valueOf() ?? 0;

/**
 * Calculate which "prediction week" a given date falls into.
 * Week 1 = Dec 5-11, Week 2 = Dec 12-18, etc.
 */
function getPredictionWeekFromDate(date: moment.Moment | string): number {
  if (!WEEK_ONE_START_ENABLED || !WEEK_ONE_START) return 0;
  const d = moment(date);
  if (!d.isValid()) return 0;
  const diffDays = d.diff(WEEK_ONE_START, 'days');
  if (diffDays < 0) return 0;
  return Math.floor(diffDays / 7) + 1;
}

/**
 * Get the start and end dates for a given prediction week.
 * Returns { start: Friday 00:00, end: Thursday 23:59:59 }
 */
function getPredictionWeekDateRange(predictionWeek: number): { start: moment.Moment; end: moment.Moment } {
  if (!WEEK_ONE_START_ENABLED || !WEEK_ONE_START) {
    throw new Error('getPredictionWeekDateRange called while WEEK_ONE_START feature is disabled');
  }
  const start = WEEK_ONE_START.clone().add((predictionWeek - 1) * 7, 'days').startOf('day');
  const end = start.clone().add(6, 'days').endOf('day');
  return { start, end };
}

/** WeekId of the gameweek "now" falls in: last match with kick-off ≤ now (same as prono-dpg-ui). */
function getActivePlayWeekIdFromMatches(matches: any[], now: moment.Moment): number | null {
  const dated = matches.filter(
    (m) => m != null && m.weekId != null && getMatchUtcMoment(m.date) !== null
  );
  if (dated.length === 0) return null;
  const sorted = [...dated].sort((a, b) => getMatchTimeValue(a.date) - getMatchTimeValue(b.date));
  const notAfterNow = sorted.filter((m) => {
    const matchMoment = getMatchUtcMoment(m.date);
    return matchMoment ? !matchMoment.isAfter(now) : false;
  });
  if (notAfterNow.length > 0) {
    return Number(notAfterNow[notAfterNow.length - 1]!.weekId);
  }
  return Number(sorted[0]!.weekId);
}

/** First / last match date in the selected week (e.g. "11–19 juin"). */
function formatMatchWeekDateRange(matches: any[], loc: string): string | null {
  const dates = matches
    .map((m) => getMatchLocalMoment(m?.date)?.locale(loc) ?? null)
    .filter((m): m is moment.Moment => m !== null && m.isValid())
    .sort((a, b) => a.valueOf() - b.valueOf());
  if (dates.length === 0) return null;
  const start = dates[0]!;
  const end = dates[dates.length - 1]!;
  if (start.isSame(end, "day")) {
    return start.format("D MMMM");
  }
  if (start.month() === end.month() && start.year() === end.year()) {
    return `${start.format("D")}–${end.format("D MMMM")}`;
  }
  if (start.year() === end.year()) {
    return `${start.format("D MMMM")} – ${end.format("D MMMM")}`;
  }
  return `${start.format("D MMMM YYYY")} – ${end.format("D MMMM YYYY")}`;
}

function isFutureGameweek(
  weeks: Week[],
  selectedWeekId: number,
  activePlayWeekId: number | null
): boolean {
  if (activePlayWeekId == null || weeks.length === 0) return false;
  const activeIdx = weeks.findIndex((w) => w.weekId === activePlayWeekId);
  const selectedIdx = weeks.findIndex((w) => w.weekId === selectedWeekId);
  if (activeIdx < 0 || selectedIdx < 0) return false;
  return selectedIdx > activeIdx;
}

const pickFromApiPrediction = (prediction: ApiPredictionItem): PickValue => {
  const outcome = prediction.result?.outcome ?? null;
  const scoreMatch = outcome?.match(/^(\d+)-(\d+)$/);
  const homeGoals = scoreMatch ? Number(scoreMatch[1]) : null;
  const awayGoals = scoreMatch ? Number(scoreMatch[2]) : null;

  return {
    winnerId: prediction.result?.winnerId ?? null,
    outcome,
    round: prediction.result?.round ?? null,
    booster: !!prediction.booster,
    homeGoals,
    awayGoals,
  };
};

const getPickResult = (pick: PickValue): ApiResult | null => {
  if (typeof pick.homeGoals !== "number" || typeof pick.awayGoals !== "number") {
    return null;
  }

  return {
    winnerId: pick.winnerId ?? null,
    outcome: `${pick.homeGoals}-${pick.awayGoals}`,
    round: pick.round ?? 1,
  };
};

const EMPTY_ODDS = Object.freeze({ home: 0, draw: 0, away: 0 });

const parseOdds = (rawOdds: any) => {
  if (!rawOdds) return EMPTY_ODDS;
  try {
    const parsed = typeof rawOdds === 'string' ? JSON.parse(rawOdds) : rawOdds;
    return {
      home: parsed['1'] ?? parsed.home ?? 0,
      draw: parsed['X'] ?? parsed.draw ?? 0,
      away: parsed['2'] ?? parsed.away ?? 0,
    };
  } catch (e) {
    console.error("Failed to parse odds", rawOdds, e);
    return EMPTY_ODDS;
  }
};

// ---------------------- Week Switch ----------------------
const WeekSwitch: React.FC<WeekSwitchProps> = ({ competitionData }) => {
  const { weekId, setWeekId } = useCompetitionContext();
  const { t } = useTranslation();

  const weeks = competitionData.weeks || [];
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const currentPath = location.pathname;

  if (weeks.length === 0) {
    return <div>No weeks available</div>;
  }

  const isRootRoute = currentPath === "/fantasy" || currentPath === "/fantasy/";
  const isEditRoute = currentPath.startsWith("/fantasy/edit/");
  const isPointsRoute =
    currentPath.startsWith("/fantasy/points/") ||
    currentPath.startsWith("/fantasy/public/");
  const isPublicRoute = currentPath.startsWith("/fantasy/public/");
  const showWeekSwitch =
    !currentPath.startsWith("/fantasy/leagues") &&
    !currentPath.startsWith("/fantasy/calendar") &&
    !currentPath.startsWith("/fantasy/rankings");


  useEffect(() => {
    if (weekId === 0) {
      const defaultWeekId = isPointsRoute
        ? competitionData.deadlineInfo.deadlineWeek
          ? Math.max(competitionData.deadlineInfo.deadlineWeek - 1, 1)
          : competitionData.deadlineInfo.rosterEndWeek
        : isRootRoute &&
          competitionData.deadlineInfo.displayWeek <
            competitionData.deadlineInfo.deadlineWeek
        ? competitionData.deadlineInfo.displayWeek
        : competitionData.deadlineInfo.deadlineWeek
        ? competitionData.deadlineInfo.deadlineWeek
        : competitionData.deadlineInfo.rosterEndWeek;

      setWeekId(defaultWeekId);
      // console.log('DEFAULT WEEK ID', defaultWeekId);
    }
  }, [weekId, isPointsRoute, setWeekId, competitionData]);

  const currentWeekIndex =
    weeks.findIndex((week: Week) => week.weekId === weekId) ?? 0;
    
  // console.log('CURRENT WEEK INDEX', currentWeekIndex);
  // console.log('WEEKS', weeks);
  const goToPreviousWeek = () => {
    if (currentWeekIndex > 0) {
      const newWeekId = weeks[currentWeekIndex - 1]!.weekId;
      setWeekId(newWeekId);
      // console.log('NEW WEEK ID', newWeekId);
      if (
        isEditRoute &&
        newWeekId < competitionData.deadlineInfo.deadlineWeek
      ) {
        navigate(`/fantasy/points/${params.id}`);
      }
    }
  };

  const goToNextWeek = () => {
    if (currentWeekIndex < weeks.length - 1) {
      const newWeekId = weeks[currentWeekIndex + 1]!.weekId;
      setWeekId(newWeekId);
      // console.log('NEW WEEK ID', newWeekId);
      if (
        competitionData.deadlineInfo.deadlineWeek &&
        isPointsRoute &&
        newWeekId >= competitionData.deadlineInfo.deadlineWeek
      ) {
        navigate(`/fantasy/edit/${params.id}`);
      }
    }
  };

  const phase =
    weekId > 3
      ? t("predictions.phaseKnockout")
      : t("predictions.phaseGroup");

  const weekTitle = t("predictions.weekSwitchTitle")
    .replace("{week}", String(weekId))
    .replace("{phase}", phase);
  const weekTitleMobile = t("predictions.weekSwitchTitleMobile")
    .replace("{week}", String(weekId))
    .replace("{phase}", phase);

  return (
    showWeekSwitch && (
      <div className="bg-white text-[var(--rtbf-dark,#001425)] rounded-none h-12 w-full flex items-center justify-between px-4 py-3 shadow-[0px_5px_15px_0px_rgba(0,0,0,0.10)] lg:grid lg:grid-cols-[1fr_auto_1fr]">
        <button
          type="button"
          onClick={goToPreviousWeek}
          disabled={
            currentWeekIndex === 0
          }
          aria-label={t("predictions.weekSwitchPrevious")}
          className="relative shrink-0 size-6 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed lg:justify-self-start"
        >
          <img src="/figma-assets/35911290705663781d6a58bc146bf2e044f9e66b.svg" alt="" className="absolute inset-0 m-auto size-6 rotate-180" />
        </button>
        <div className="basis-0 flex flex-col font-bold grow justify-center leading-[0] min-h-px min-w-px relative shrink-0 text-[15px] text-[var(--rtbf-dark,#001425)] text-center lg:col-start-2">
          <p className="leading-[normal]">
            <span className="sm:hidden">{weekTitleMobile}</span>
            <span className="hidden sm:inline">{weekTitle}</span>
          </p>
        </div>
        <button
          type="button"
          onClick={goToNextWeek}
          disabled={
            currentWeekIndex === weeks.length - 1 ||
            (isPublicRoute &&
              currentWeekIndex + 2 >=
                competitionData.deadlineInfo.deadlineWeek)
          }
          aria-label={t("predictions.weekSwitchNext")}
          className="relative shrink-0 size-6 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed lg:justify-self-end lg:col-start-3"
        >
          <img src="/figma-assets/35911290705663781d6a58bc146bf2e044f9e66b.svg" alt="" className="absolute inset-0 m-auto size-6" />
        </button>
      </div>
    )
  );
};

const lockIcon = '/assets/d7387cf39c19faaafab8d50f769cc810d7fe10fa.svg'
const hourglassIcon = '/assets/3d411c7371af14c47c77115364c66a06d0bd95cc.svg'
const checkmarkIconBg = '/assets/c435efeb682b0246a459533c3a3f16ef5bce6701.svg'
const checkmarkIconVector = '/assets/f405eb262411951f9eca93987ce2e4b914914066.svg'
const xmarkIcon = '/assets/7e436e8678e34f882259c85dcc31d049ec19bab7.svg'
const arrowRightIcon = '/assets/e5231f95e42e099ebaaec0209df96f88062208c7.svg'
const arrowLeftIcon = '/assets/cee10fbeb7cea7c5953e21073602e721b6734d26.svg'
const caretUpIconGreen = '/assets/5fbeccc49311b3f686586ffb4777d8a076d8a279.svg'
const caretDownIconRed = '/assets/00040d8b8778d31c91e4be468f4652d0c1abb7f0.svg'
const leagueIconSvg = '/assets/a74a835e4b8b67d4a1c6b10484100622d7c07222.svg'
const emptyIcon = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTYiIGN5PSIxNiIgcj0iMTYiIGZpbGw9IiNFMEUwRTAiLz4KPHBhdGggZD0iTTkgMTZMMTQgMjFMMjMgMTIiIHN0cm9rZT0iI0M4QzhDOCIgc3Ryb2tlLXdpZHRoPSIzIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPC9zdmc+'

// Streak background from Figma
const streakBg = '/assets/08ad33feace27a993b49a609da61c5e4962b1a0d.png'

const STREAK_DAY_LABELS = ["Fr", "Sa", "Su", "Mo", "Tu", "We", "Th"] as const;

type StreakDayStatus = "completed" | "broken" | "empty";

type StreakDayConfig = {
  day: (typeof STREAK_DAY_LABELS)[number];
  status: StreakDayStatus;
};

const buildDefaultStreakDays = (): StreakDayConfig[] =>
  STREAK_DAY_LABELS.map((day) => ({
    day,
    status: "empty" as StreakDayStatus,
  }));

type StreakMatchMeta = {
  matchId: number;
  dateMoment: moment.Moment;
  hasPick: boolean;
};

type StreakDayProps = {
  className?: string;
  day: string;
  status?: StreakDayStatus;
};

function StreakDay({ className, day, status = "empty" }: StreakDayProps) {
  // completed = green checkmark, broken = red xmark, empty = light grey checkmark (upcoming)
  const icon = status === "completed" ? checkmarkIconVector : status === "broken" ? xmarkIcon : emptyIcon;
  
  return (
    <div className={className}>
      <div className="flex flex-col font-bold h-[32px] justify-center leading-[0] text-[16px] text-[#868889] text-center w-full">
        <p className="leading-[18px]">{day}</p>
      </div>
      <div className="relative size-[32px]">
        {status === "completed" && (
          <>
            <div className="absolute inset-0">
              <img alt="" className="block max-w-none size-full" src={checkmarkIconBg} />
            </div>
            <div className="absolute inset-0">
              <img alt="" className="block max-w-none size-full" src={checkmarkIconVector} />
            </div>
          </>
        )}
        {status === "broken" && (
          <>
            <div className="absolute inset-0">
              <img alt="" className="block max-w-none size-full" src={checkmarkIconBg} />
            </div>
            <div className="absolute inset-0">
              <img alt="" className="block max-w-none size-full" src={xmarkIcon} />
            </div>
          </>
        )}
        {status === "empty" && emptyIcon && (
          <div className="absolute inset-0">
            <img alt="" className="block max-w-none size-full" src={emptyIcon} />
          </div>
        )}
      </div>
    </div>
  );
}

type StreakWeekProps = {
  days?: StreakDayConfig[];
};

function StreakWeek({ days = buildDefaultStreakDays() }: StreakWeekProps) {
  return (
    <div className="flex gap-[8px] items-center">
      {days.map((d, i) => (
        <StreakDay key={i} className="flex flex-col items-start w-[32px]" day={d.day} status={d.status} />
      ))}
    </div>
  );
}

type StreakProps = {
  className?: string;
  streak?: "completed" | "broken" | "ongoing";
  days?: StreakDayConfig[];
};

function Streak({ className, streak = "completed", days = buildDefaultStreakDays() }: StreakProps) {
  const { t } = useTranslation();
  const message = streak === "broken" 
    ? t('predictions.streakBroken')
    : t('predictions.streakMessage');
  
  return (
    <div className={`${className} relative w-full flex flex-col gap-2 items-center  py-5 rounded-2xl overflow-hidden`}>
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute bg-black inset-0 rounded-2xl" />
        <img alt="" className="absolute max-w-none object-center object-cover size-full rounded-2xl" src={streakBg} />
        <div className="absolute bg-black/30 inset-0 rounded-2xl" />
      </div>
      <div className="flex flex-col font-['PanamaBold'] h-6 justify-center leading-[0] text-[16px] text-white text-center w-full relative z-10 flex-shrink-0">
        <p className="leading-[18px]">{t('predictions.keepStreak')}</p>
      </div>
      <div className="flex flex-col font-normal justify-center leading-[0] text-[14px] text-white relative z-10 flex-shrink-0 w-[266px] h-[36px]">
        <p className="leading-[18px]">{message}</p>
      </div>
      <div className="relative z-10 flex-shrink-0">
        <StreakWeek days={days} />
      </div>
    </div>
  );
}

type PredictionProgressCardProps = {
  remaining: number;
  total: number;
  doneLabel: string;
  leftLabel: (remaining: number) => string;
};

function PredictionProgressCard({
  remaining,
  total,
  doneLabel,
  leftLabel,
}: PredictionProgressCardProps) {
  if (total <= 0) {
    return null;
  }

  const done = Math.max(total - remaining, 0);
  const isDone = remaining === 0;
  const progressPercent = Math.max(0, Math.min(100, (done / total) * 100));
  const label = isDone ? doneLabel : leftLabel(remaining);

  return (
    <div className="w-full overflow-hidden rounded-[8px] bg-[var(--rtbf-blue-dark,#003E63)] px-4 py-3 shadow-[0px_5px_15px_0px_rgba(0,0,0,0.10)]">
      <p className="text-[12px] font-normal leading-normal text-[#C4D1EA]">{label}</p>
      <div className="mt-2 h-[2px] w-full rounded-[4px] bg-white/15">
        <div
          className={`h-[2px] rounded-[4px] ${isDone ? "bg-[var(--rtbf-green,#00b400)]" : "bg-[var(--rtbf-orange,#fdc300)]"}`}
          style={{ width: `${progressPercent}%` }}
        />
      </div>
    </div>
  );
}

type PointsOverviewCardProps = {
  headlineWeekScore: number | string | null | undefined;
  dailyRank: number | string | null | undefined;
  totalPoints: number | string | null | undefined;
  overallRank: number | string | null | undefined;
  isLoading?: boolean;
};

function PointsOverviewCard({
  headlineWeekScore,
  dailyRank,
  totalPoints,
  overallRank,
  isLoading = false,
}: PointsOverviewCardProps) {
  const formatOverviewValue = (value: number | string | null | undefined) => {
    if (isLoading) return "...";
    if (value === null || value === undefined || value === "") return "–";
    const numericValue = typeof value === "string" ? Number(value) : value;
    if (!Number.isFinite(numericValue)) return String(value);
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(numericValue);
  };

  return (
    <div className="flex items-center justify-center">
      <div className="flex items-center">
        <div className="flex h-[96px] flex-col items-center justify-center px-4">
          <div className="flex h-[72px] w-[72px] flex-col items-center justify-center rounded-[4px] bg-[var(--rtbf-orange,#fdc300)] text-[var(--rtbf-dark,#001425)]">
            <p className="text-[24px] leading-none">{formatOverviewValue(headlineWeekScore)}</p>
            <p className="text-[12px] leading-none">points</p>
          </div>
        </div>
        <div className="flex w-[185px] flex-col gap-1 text-[12px] leading-none">
          <div className="flex h-[21px] items-center justify-between rounded-[4px] bg-white px-2 py-1">
            <span className="text-[rgba(0,0,0,0.65)]">Classement du jour</span>
            <span className="font-medium text-[var(--rtbf-blue-dark,#003e63)]">{formatOverviewValue(dailyRank)}</span>
          </div>
          <div className="flex h-[21px] items-center justify-between rounded-[4px] bg-[var(--light,#f0f5f9)] px-2 py-1">
            <span className="text-[rgba(0,0,0,0.65)]">Points au total</span>
            <span className="font-medium text-[var(--rtbf-blue-dark,#003e63)]">{formatOverviewValue(totalPoints)}</span>
          </div>
          <div className="flex h-[21px] items-center justify-between rounded-[4px] bg-white px-2 py-1">
            <span className="text-[rgba(0,0,0,0.65)]">Classement général</span>
            <span className="font-medium text-[var(--rtbf-blue-dark,#003e63)]">{formatOverviewValue(overallRank)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------- Predictions Page ----------------------
const Predictions: FC<PredictionsProps> = ({ weekId, user }) => {
  const { t, locale } = useTranslation();
  const { competitionConfig, competitionData } = useCompetitionContext();
  const showFavoriteTeamWidget = false;
  const showPrizesWidget = false;
  const showPredictionProgress = false;
  const favoriteClubId =
    user?.favClub?.id !== undefined && user?.favClub?.id !== null
      ? Number(user.favClub.id)
      : null;
  const normalizedUserId =
    user?.id !== undefined && user?.id !== null ? String(user.id) : null;

  // Check if user is authenticated
  const isAuthenticated = !!user?.id;
  const showSummaryWidgets = false;
  const NEXT_UP_CUTOFF_HOUR = 12;

  // State for auth modal
  const [showAuthModal, setShowAuthModal] = useState(false);
  const mockedNow = useMemo(() => {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const mockNowParam = params.get("mockNow");
    if (!mockNowParam) return null;
    // URLSearchParams decodes "+" as space; normalize so offsets like +02:00 remain valid.
    const normalizedMockNow = mockNowParam.includes(" ") && !mockNowParam.includes("+")
      ? mockNowParam.replace(" ", "+")
      : mockNowParam;
    const parsed = moment(normalizedMockNow);
    return parsed.isValid() ? parsed : null;
  }, []);
  const getNow = useCallback(() => (mockedNow ? mockedNow.clone() : moment()), [mockedNow]);

  // Handler for when authentication is required (user tries to make a prediction without being logged in)
  const handleAuthRequired = useCallback(() => {
    setShowAuthModal(true);
  }, []);

  // Handler for sign in button in modal
  const handleSignIn = useCallback(() => {
    track('Signup');
    // Go straight to the IdP. The onboarding form is reached via the
    // post-auth callbackUrl when the freshly-authenticated user has no
    // username yet; returning users with a username are redirected
    // transparently to /fantasy/predictions.
    signIn('rtbf', {
      callbackUrl: '/onboarding/username?next=/fantasy/predictions',
    });
  }, []);

  // Debug logging
  useEffect(() => {
    console.log('[Predictions] Component mounted/updated:', {
      weekId,
      userId: user?.id,
      userEmail: user?.email,
      userName: user?.username,
      competitionFeed: competitionConfig?.competitionFeed,
      hasCompetitionData: !!competitionData,
      matchesCount: competitionData?.matches?.length ?? 0,
    });
  }, [weekId, user, competitionConfig, competitionData]);

  const { data: favoriteClubMatchesData } = useQuery<ClubMatchesResponse>({
    queryKey: [
      "favorite-club-matches",
      competitionConfig?.seasonId,
      favoriteClubId,
    ],
    queryFn: () =>
      fetchClubMatches(competitionConfig.seasonId, favoriteClubId!),
    enabled: Boolean(
      favoriteClubId &&
        competitionConfig?.seasonId &&
        competitionConfig?.competitionFeed
    ),
    staleTime: 60_000,
  });

  // Matches for current week 
  console.log('competitionData', competitionData);
  const weekMatches = useMemo(() => {
    return (competitionData.matches || [])
      .filter((m: any) => m.weekId === weekId)
      .filter((m: any) => m?.home?.id && m?.away?.id)
      .map((match: any) => {
        const matchStats = competitionData.stats?.find?.((s: any) => s?.m === match.id);
        return {
          ...match,
          stats: matchStats ?? null,
        };
      });
  }, [competitionData.matches, competitionData.stats, weekId]);

  /** True once the earliest kick-off on the first calendar "matchday" of this week has passed. */
  const firstMatchOfFirstMatchdayHasStarted = useMemo(() => {
    const now = moment();
    const firstKickoffByDay = new Map<string, moment.Moment>();

    for (const match of weekMatches) {
      if (!match?.date) continue;
      const kickoff = getMatchLocalMoment(match.date);
      if (!kickoff?.isValid()) continue;
      const dayKey = kickoff.clone().startOf("day").format("YYYY-MM-DD");
      const existing = firstKickoffByDay.get(dayKey);
      if (!existing || kickoff.isBefore(existing)) {
        firstKickoffByDay.set(dayKey, kickoff);
      }
    }

    if (firstKickoffByDay.size === 0) return false;

    const firstCalendarDayKey = Array.from(firstKickoffByDay.keys()).sort()[0]!;
    const firstKickoffThatDay = firstKickoffByDay.get(firstCalendarDayKey);
    if (!firstKickoffThatDay) return false;

    return !firstKickoffThatDay.isAfter(now);
  }, [weekMatches]);

  const favoriteClubExtras = useMemo(() => {
    if (!WEEK_ONE_START_ENABLED || !WEEK_ONE_START) {
      return [];
    }
    if (!showFavoriteTeamWidget) {
      return [];
    }
    if (!favoriteClubMatchesData?.matches?.length || !favoriteClubId) {
      return [];
    }

    // Week 1 starts on the date in NEXT_PUBLIC_WEEK_ONE_START.
    // Each subsequent week starts 7 days later.
    const weekStart = WEEK_ONE_START.clone().add((weekId - 1) * 7, "days");
    const weekEnd = weekStart.clone().add(6, "days").endOf("day");
    
    const seenMatchIds = new Set(weekMatches.map((match: any) => match.id));

    const matchesInWindow = favoriteClubMatchesData.matches
      .filter((match: any) => {
        if (!match?.date) return false;
        const matchMoment = getMatchLocalMoment(match.date);
        return matchMoment?.isBetween(weekStart, weekEnd, undefined, "[]") ?? false;
      })
      .filter((match: any) => !seenMatchIds.has(match.id))
      .sort((a: any, b: any) => {
        const aTime = getMatchTimeValue(a?.date);
        const bTime = getMatchTimeValue(b?.date);
        return aTime - bTime;
      });

    return matchesInWindow.slice(0, 1);
  }, [favoriteClubMatchesData?.matches, favoriteClubId, weekMatches, weekId, showFavoriteTeamWidget]);

  const matchesToRender = useMemo(
    () => [...weekMatches, ...favoriteClubExtras],
    [weekMatches, favoriteClubExtras]
  );

  // Pre-compute parsed odds per match.id so each MatchCard receives the same
  // object reference across renders. Without this, `odds={parseOdds(match.odds)}`
  // creates a fresh literal on every render and defeats React.memo on MatchCard.
  const oddsByMatchId = useMemo(() => {
    const map = new Map<number, ReturnType<typeof parseOdds>>();
    matchesToRender.forEach((m: any) => {
      map.set(m.id, parseOdds(m.odds));
    });
    return map;
  }, [matchesToRender]);

  // Split matches into upcoming and past, sorted appropriately
  const { upcomingMatches, pastMatches } = useMemo(() => {
    const now = moment();
    const upcoming: any[] = [];
    const past: any[] = [];

    matchesToRender.forEach((match: any) => {
      if (!match?.date) {
        // If no date, treat as upcoming
        upcoming.push(match);
        return;
      }

      const matchMoment = getMatchUtcMoment(match.date);
      if (!matchMoment?.isValid()) {
        // Invalid date, treat as upcoming
        upcoming.push(match);
        return;
      }

      if (matchMoment.isAfter(now)) {
        upcoming.push(match);
      } else {
        past.push(match);
      }
    });

    // Sort upcoming: earliest first (ascending)
    upcoming.sort((a: any, b: any) => {
      const aTime = getMatchTimeValue(a?.date);
      const bTime = getMatchTimeValue(b?.date);
      return aTime - bTime;
    });

    // Sort past: chronological (same as prono-dpg-ui)
    past.sort((a: any, b: any) => {
      const aTime = getMatchTimeValue(a?.date);
      const bTime = getMatchTimeValue(b?.date);
      return aTime - bTime;
    });

    return { upcomingMatches: upcoming, pastMatches: past };
  }, [matchesToRender]);

  const buildMatchGroups = useCallback((matches: any[]) => {
    type Group = { key: string; label: string; matches: any[] };
    const groupedByDay = new Map<string, { day: moment.Moment; matches: any[] }>();

    matches.forEach((match: any) => {
      if (!match?.date) return;
      const day = getMatchLocalMoment(match.date)?.startOf("day");
      if (!day?.isValid()) return;
      const key = day.format("YYYY-MM-DD");
      const existing = groupedByDay.get(key);
      if (existing) {
        existing.matches.push(match);
      } else {
        groupedByDay.set(key, { day, matches: [match] });
      }
    });

    return Array.from(groupedByDay.entries())
      .sort(([a], [b]) => moment(a).valueOf() - moment(b).valueOf())
      .map(([key, value]): Group => {
        const label = value.day.locale("fr").format("dddd D MMMM");
        const formattedLabel = label.charAt(0).toUpperCase() + label.slice(1);
        return { key, label: formattedLabel, matches: value.matches };
      });
  }, []);

  const { nextUpMatches } = useMemo(() => {
    if (!upcomingMatches.length) {
      return { nextUpMatches: [] as any[] };
    }

    const firstUpcomingWithDate = upcomingMatches.find((match: any) => !!match?.date);
    if (!firstUpcomingWithDate?.date) {
      return { nextUpMatches: upcomingMatches };
    }

    const baseDay = getMatchLocalMoment(firstUpcomingWithDate.date)?.startOf("day");
    if (!baseDay?.isValid()) {
      return { nextUpMatches: upcomingMatches };
    }

    const nextUpWindowEnd = baseDay
      .clone()
      .add(1, "day")
      .hour(NEXT_UP_CUTOFF_HOUR)
      .minute(0)
      .second(0)
      .millisecond(0);

    const nextUp: any[] = [];

    upcomingMatches.forEach((match: any) => {
      if (!match?.date) {
        return;
      }

      const matchMoment = getMatchLocalMoment(match.date);
      if (!matchMoment?.isValid()) {
        return;
      }

      if (matchMoment.isBetween(baseDay, nextUpWindowEnd, undefined, "[)")) {
        nextUp.push(match);
      }
    });

    return { nextUpMatches: nextUp };
  }, [upcomingMatches]);

  const nextUpMatchGroups = useMemo(
    () => buildMatchGroups(nextUpMatches),
    [buildMatchGroups, nextUpMatches]
  );
  const nextUpFlatMatches = useMemo(
    () => nextUpMatchGroups.flatMap((group) => group.matches),
    [nextUpMatchGroups]
  );
  const remainingMatchIds = useMemo(
    () => new Set(nextUpFlatMatches.map((match: any) => match.id)),
    [nextUpFlatMatches]
  );
  const remainingMatchesChronological = useMemo(() => {
    return matchesToRender
      .filter((match: any) => !remainingMatchIds.has(match.id))
      .sort((a: any, b: any) => {
        if (!a?.date && !b?.date) return 0;
        if (!a?.date) return 1;
        if (!b?.date) return -1;
        return getMatchTimeValue(a.date) - getMatchTimeValue(b.date);
      });
  }, [matchesToRender, remainingMatchIds]);
  const remainingFutureMatches = useMemo(() => {
    const now = moment();
    return remainingMatchesChronological.filter((match: any) => {
      if (!match?.date) return true;
      const matchMoment = getMatchUtcMoment(match.date);
      return matchMoment?.isValid() ? matchMoment.isAfter(now) : true;
    });
  }, [remainingMatchesChronological]);
  const remainingFinishedMatches = useMemo(() => {
    const now = moment();
    return remainingMatchesChronological
      .filter((match: any) => {
        if (!match?.date) return false;
        const matchMoment = getMatchUtcMoment(match.date);
        return matchMoment?.isValid() ? !matchMoment.isAfter(now) : false;
      })
      .sort((a: any, b: any) => {
        if (!a?.date && !b?.date) return 0;
        if (!a?.date) return 1;
        if (!b?.date) return -1;
        return getMatchTimeValue(a.date) - getMatchTimeValue(b.date);
      });
  }, [remainingMatchesChronological]);

  // Fetch predictions (latest per user/match) - only when authenticated
  const {
    data: predictionsData,
    isLoading: isLoadingPredictions,
  } = useQuery<PredictionsResponse>({
    queryKey: [
      "predictions",
      competitionConfig?.competitionFeed,
      competitionConfig?.seasonId,
      normalizedUserId,
    ],
    queryFn: () =>
      fetchPredictions(
        competitionConfig.competitionFeed,
        competitionConfig.seasonId
      ),
    enabled: Boolean(
      normalizedUserId &&
        competitionConfig?.competitionFeed &&
        competitionConfig?.seasonId
    ),
    staleTime: 30_000,
  });

  const {
    data: userRanksData,
    isLoading: isLoadingUserRanks,
  } = useQuery<UserRanksResponse>({
    queryKey: [
      "user-ranks",
      competitionConfig.competitionFeed,
      competitionConfig.seasonId,
      normalizedUserId,
      weekId,
    ],
    queryFn: () =>
      fetchUserRanks(
        competitionConfig.competitionFeed,
        competitionConfig.seasonId,
        normalizedUserId!,
        weekId
      ),
    enabled: Boolean(
      normalizedUserId &&
        competitionConfig?.competitionFeed &&
        competitionConfig?.seasonId
    ),
    staleTime: 30_000,
  });

  // Local state: picks & boosters per week for UI
  // We keep the UI "pick" object with a booster flag, but when sending to API
  // we split booster (top-level) and result without booster.
  const [picksByWeek, setPicksByWeek] = useState<
    Record<number, Record<number, PickValue>>
  >({});
  const [savingMatchIds, setSavingMatchIds] = useState<Set<number>>(new Set());
  // Track per-match save status used to drive the inline checkmark indicator.
  // dirty = both goals filled but not yet committed to the server (debounce or in-flight).
  // saved = both goals filled and the latest payload was acknowledged by the server.
  const [dirtyMatchIds, setDirtyMatchIds] = useState<Set<number>>(new Set());
  const [savedMatchIds, setSavedMatchIds] = useState<Set<number>>(new Set());
  // Per-match debounce timers (one save scheduled per match at a time).
  const debounceTimersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  // Per-match in-flight tracker: if a save is in flight when a new edit comes in,
  // we hold the latest payload and dispatch it once the in-flight save resolves.
  const pendingPayloadsRef = useRef<Map<number, PickValue>>(new Map());
  const inFlightMatchIdsRef = useRef<Set<number>>(new Set());
  // Match IDs whose latest keystroke transitioned the card from incomplete to
  // complete and which are now waiting for a successful save to advance focus
  // to the next incomplete card. See "Deferred cross-card focus advance" below
  // and ADR 0001.
  const pendingAdvanceMatchIdsRef = useRef<Set<number>>(new Set());

  // Sync API to local
  useEffect(() => {
    const next: Record<number, Record<number, PickValue>> = {};
    const savedIds = new Set<number>();

    if (predictionsData?.predictions) {
      predictionsData.predictions.forEach((p: ApiPredictionItem) => {
        const w = p.weekId;
        if (!next[w]) next[w] = {};
        const pick = pickFromApiPrediction(p);
        next[w][p.matchId] = pick;
        if (typeof pick.homeGoals === "number" && typeof pick.awayGoals === "number") {
          savedIds.add(p.matchId);
        }
      });
    }

    setPicksByWeek(next);
    setSavedMatchIds(savedIds);
  }, [predictionsData]);

  // Clear all pending debounce timers on unmount to avoid orphan saves after navigation.
  useEffect(() => {
    return () => {
      debounceTimersRef.current.forEach((timer) => clearTimeout(timer));
      debounceTimersRef.current.clear();
      pendingPayloadsRef.current.clear();
      pendingAdvanceMatchIdsRef.current.clear();
    };
  }, []);

  // Score-input focus retention across save-driven re-renders. See ADR 0001.
  //
  // After the cross-card advance lands on the next empty card's home input,
  // the user is parked there waiting to type. ~400 ms later the previous
  // card's debounce fires and `flushSave` flips three pieces of state in
  // quick succession (savingMatchIds add -> await -> savingMatchIds remove
  // + dirtyMatchIds remove + savedMatchIds add). Each commit re-renders
  // every MatchCard. The third commit in particular — the one that flips
  // the previous card's checkmark from yellow "pending" to green "saved" —
  // can drop focus on the empty input the user is parked on, leaving them
  // staring at a "+" placeholder with no caret.
  //
  // We track the most-recently-focused score input by *logical identity*
  // (matchId + side) rather than DOM reference, because the DOM element
  // can in principle be re-created across commits — a stored DOM ref would
  // then be detached and `.focus()`'ing it would silently no-op. Looking
  // the input up by data-attribute via `querySelector` always finds the
  // current element.
  //
  // The layout effect runs after every commit (no dep array). It's cheap —
  // one ref read, one querySelector, one activeElement check — and the
  // "active is a real focusable element" guard early-returns the common
  // case where focus is exactly where it should be. The only cost is on
  // commits that *did* drop focus, where we re-focus the tracked input.
  // The trade-off (a click on a non-focusable element during a save
  // window pulls focus back to the last score input) is the same "always
  // advance" trade-off documented in the ADR.
  const lastFocusedScoreRef = useRef<{
    matchId: number;
    side: "home" | "away";
  } | null>(null);
  useEffect(() => {
    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target || target.tagName !== "INPUT") return;
      const homeId = target.getAttribute("data-match-home-input");
      if (homeId) {
        lastFocusedScoreRef.current = { matchId: Number(homeId), side: "home" };
        return;
      }
      const awayId = target.getAttribute("data-match-away-input");
      if (awayId) {
        lastFocusedScoreRef.current = { matchId: Number(awayId), side: "away" };
      }
    };
    document.addEventListener("focusin", handleFocusIn);
    return () => {
      document.removeEventListener("focusin", handleFocusIn);
    };
  }, []);
  const restoreScoreInputFocusIfDropped = useCallback(() => {
    const last = lastFocusedScoreRef.current;
    if (!last) return;
    const selector =
      last.side === "home"
        ? `input[data-match-home-input="${last.matchId}"]`
        : `input[data-match-away-input="${last.matchId}"]`;
    const target = document.querySelector<HTMLInputElement>(selector);
    // No target this commit (card is momentarily not in the DOM, e.g. between
    // an unmount and a re-mount during reconciliation). Leave the ref intact
    // so the next commit gets another shot — the next focusin on a score
    // input will overwrite it if the user has moved on.
    if (!target) return;
    const active = document.activeElement;
    if (active === target) return; // already focused, nothing to do
    // Only restore when focus has been dropped to the page-level "no focus"
    // state. Browsers report this as `body`, the document element, or null.
    // Anything else (button, link, another input) means the user moved
    // focus deliberately, and we leave them alone.
    const focusLost =
      !active ||
      active === document.body ||
      active === document.documentElement;
    if (!focusLost) return;
    target.focus();
  }, []);
  // Layout-effect pass: catch focus drops that happen during the synchronous
  // commit (DOM-mutation phase). Restore before the browser paints, so the
  // user sees no flicker.
  useLayoutEffect(() => {
    restoreScoreInputFocusIfDropped();
  });
  // Effect pass: catch focus drops that land in the microtask / paint window
  // after `useLayoutEffect` has already run (some browsers / React effects
  // can blur focused elements as part of post-commit cleanup work). This is
  // a second-chance safety net — if the layout pass already restored focus,
  // the early-return on `active === target` makes this a no-op.
  useEffect(() => {
    restoreScoreInputFocusIfDropped();
  });

  // derived week picks
  const picks = picksByWeek[weekId] || {};

  // Booster capacity calculation using date-based weeks (Friday-Thursday)
  // A user gets +1 booster capacity if they predicted ALL matches in the previous week.
  // Entire feature is gated on WEEK_ONE_START_ENABLED.
  const currentPredictionWeek = useMemo(() => {
    if (!WEEK_ONE_START_ENABLED) return 0;
    const now = moment();
    return getPredictionWeekFromDate(now);
  }, []);

  const previousWeekRange = useMemo(() => {
    if (!WEEK_ONE_START_ENABLED) return null;
    if (currentPredictionWeek <= 1) return null;
    return getPredictionWeekDateRange(currentPredictionWeek - 1);
  }, [currentPredictionWeek]);

  // Get all matches from the previous week (league matches + favorite club matches)
  const prevWeekAllMatches = useMemo(() => {
    if (!WEEK_ONE_START_ENABLED) return [];
    if (!previousWeekRange) return [];
    const { start, end } = previousWeekRange;
    
    // Include all competition matches in date range
    const leagueMatches = (competitionData.matches || [])
      .filter((m: any) => {
        if (!m?.date || !m?.home?.id || !m?.away?.id) return false;
        const matchMoment = getMatchLocalMoment(m.date);
        return matchMoment?.isBetween(start, end, undefined, '[]') ?? false;
      });
    
    const leagueMatchIds = new Set(leagueMatches.map((m: any) => m.id));
    
    // Include favorite club matches in date range (excluding duplicates)
    const favClubMatches = (favoriteClubMatchesData?.matches || [])
      .filter((m: any) => {
        if (!m?.date || !m?.home?.id || !m?.away?.id) return false;
        // Exclude if already in league matches
        if (leagueMatchIds.has(m.id)) return false;
        const matchMoment = getMatchLocalMoment(m.date);
        return matchMoment?.isBetween(start, end, undefined, '[]') ?? false;
      });
    
    return [...leagueMatches, ...favClubMatches];
  }, [competitionData.matches, favoriteClubMatchesData?.matches, previousWeekRange]);

  const prevWeekMatchCount = prevWeekAllMatches.length;

  // Count predictions for matches in the previous week
  const prevWeekPredCount = useMemo(() => {
    if (!WEEK_ONE_START_ENABLED) return 0;
    if (!previousWeekRange || !predictionsData?.predictions || prevWeekAllMatches.length === 0) return 0;
    
    const prevWeekMatchIds = new Set(prevWeekAllMatches.map((m: any) => m.id));
    
    return predictionsData.predictions.filter((p) => 
      prevWeekMatchIds.has(p.matchId)
    ).length;
  }, [predictionsData?.predictions, prevWeekAllMatches, previousWeekRange]);

  // User gets 2 boosters if they predicted ALL matches in the previous week, otherwise 1.
  // When WEEK_ONE_START is not configured, the carryover feature is disabled and capacity stays at 1.
  const boosterCapacity = WEEK_ONE_START_ENABLED
    ? (prevWeekMatchCount > 0 && prevWeekPredCount >= prevWeekMatchCount ? 2 : 1)
    : 1;

  // DEBUG: Log booster capacity calculation details
  useEffect(() => {
    if (!WEEK_ONE_START_ENABLED) return;
    const now = moment();
    console.log('=== BOOSTER CAPACITY DEBUG ===');
    console.log('Current date:', now.format('YYYY-MM-DD HH:mm'));
    console.log('UI weekId (from context):', weekId);
    console.log('Current prediction week (from date):', currentPredictionWeek);
    console.log('Previous prediction week:', currentPredictionWeek - 1);
    
    if (previousWeekRange) {
      console.log('Previous week date range:', {
        start: previousWeekRange.start.format('YYYY-MM-DD HH:mm'),
        end: previousWeekRange.end.format('YYYY-MM-DD HH:mm'),
      });
    } else {
      console.log('Previous week date range: null (week 1 has no previous)');
    }
    
    console.log('Total competition matches:', competitionData.matches?.length ?? 0);
    console.log('Favorite club matches:', favoriteClubMatchesData?.matches?.length ?? 0);
    console.log('Previous week matches found:', prevWeekAllMatches.length);
    console.log('Previous week match details:', prevWeekAllMatches.map((m: any) => ({
      id: m.id,
      date: m.date,
      home: m.home?.short || m.home?.name,
      away: m.away?.short || m.away?.name,
      weekId: m.weekId,
    })));
    
    console.log('Total predictions loaded:', predictionsData?.predictions?.length ?? 0);
    console.log('Predictions for previous week matches:', prevWeekPredCount);
    
    if (predictionsData?.predictions && prevWeekAllMatches.length > 0) {
      const prevWeekMatchIds = new Set(prevWeekAllMatches.map((m: any) => m.id));
      const matchingPredictions = predictionsData.predictions.filter((p) => 
        prevWeekMatchIds.has(p.matchId)
      );
      console.log('Matching predictions details:', matchingPredictions.map((p: any) => ({
        matchId: p.matchId,
        weekId: p.weekId,
        winnerId: p.result?.winnerId,
      })));
      
      // Show which matches are missing predictions
      const predictedMatchIds = new Set(matchingPredictions.map((p: any) => p.matchId));
      const missingMatches = prevWeekAllMatches.filter((m: any) => !predictedMatchIds.has(m.id));
      console.log('Matches missing predictions:', missingMatches.map((m: any) => ({
        id: m.id,
        date: m.date,
        home: m.home?.short || m.home?.name,
        away: m.away?.short || m.away?.name,
      })));
    }
    
    console.log('--- RESULT ---');
    console.log('prevWeekMatchCount:', prevWeekMatchCount);
    console.log('prevWeekPredCount:', prevWeekPredCount);
    console.log('Condition (prevWeekMatchCount > 0):', prevWeekMatchCount > 0);
    console.log('Condition (prevWeekPredCount >= prevWeekMatchCount):', prevWeekPredCount >= prevWeekMatchCount);
    console.log('Final boosterCapacity:', boosterCapacity);
    console.log('=== END BOOSTER DEBUG ===');
  }, [
    weekId, 
    currentPredictionWeek, 
    previousWeekRange, 
    prevWeekAllMatches, 
    prevWeekMatchCount, 
    prevWeekPredCount, 
    boosterCapacity, 
    competitionData.matches, 
    favoriteClubMatchesData?.matches, 
    predictionsData?.predictions
  ]);

  // Helpers to count selected boosters in current week (local state)
  const selectedBoosterIds = useMemo(
    () =>
      Object.entries(picks)
        .filter(([_, v]) => v?.booster)
        .map(([k]) => Number(k)),
    [picks]
  );

  const boostersSelected = selectedBoosterIds.length;

  // Check if booster was used on a past match
  const boosterUsedOnPastMatch = useMemo(() => {
    const now = moment();
    return pastMatches.some((match: any) => {
      const matchPick = picks[match.id];
      if (!matchPick?.booster) return false;
      
      // Check if match deadline has passed
      if (!match?.date) return false;
      const matchMoment = getMatchUtcMoment(match.date);
      if (!matchMoment?.isValid()) return false;
      
      return matchMoment.isBefore(now) || matchMoment.isSame(now);
    });
  }, [pastMatches, picks]);

  // DEBUG: Log booster selection state
  useEffect(() => {
    console.log('=== BOOSTER SELECTION DEBUG ===');
    console.log('boostersSelected:', boostersSelected);
    console.log('selectedBoosterIds:', selectedBoosterIds);
    console.log('boosterCapacity:', boosterCapacity);
    console.log('boostersLeft:', Math.max(boosterCapacity - boostersSelected, 0));
    console.log('boosterUsedOnPastMatch:', boosterUsedOnPastMatch);
    console.log('pastMatches count:', pastMatches.length);
    console.log('pastMatches with boosters:', pastMatches.filter((m: any) => picks[m.id]?.booster).map((m: any) => ({
      id: m.id,
      date: m.date,
      home: m.home?.short,
      away: m.away?.short,
      booster: picks[m.id]?.booster,
    })));
    console.log('=== END BOOSTER SELECTION DEBUG ===');
  }, [boostersSelected, selectedBoosterIds, boosterCapacity, boosterUsedOnPastMatch, pastMatches, picks]);

  const setPicks = (
    updater: (prev: Record<number, PickValue>) => Record<number, PickValue>
  ) => {
    setPicksByWeek((prev) => ({
      ...prev,
      [weekId]: updater(prev[weekId] || {}),
    }));
  };

  const autoPick = () => {
    const next: Record<number, PickValue> = {};
    weekMatches.forEach((match: any) => {
      const homeGoals = Math.floor(Math.random() * 4);
      const awayGoals = Math.floor(Math.random() * 4);
      const rand =
        homeGoals > awayGoals
          ? match.home.id
          : awayGoals > homeGoals
            ? match.away.id
            : "draw";
      next[match.id] = {
        winnerId: rand,
        outcome: `${homeGoals}-${awayGoals}`,
        round: 1,
        booster: false,
        homeGoals,
        awayGoals,
      };
    });
    setPicks(() => next);
  };

  const reset = () => setPicks(() => ({}));

  const pickedCount = Object.values(picks).filter(
    (p) => typeof p?.homeGoals === "number" && typeof p?.awayGoals === "number"
  ).length;
  const matchesRequiringPredictionCount = useMemo(
    () =>
      upcomingMatches.filter(
        (match: any) => !!(match?.home?.id && match?.away?.id)
      ).length,
    [upcomingMatches]
  );
  const remainingPredictionsCount = Math.max(
    matchesRequiringPredictionCount - pickedCount,
    0
  );

  const SAVE_DEBOUNCE_MS = 400;

  // Move keyboard focus to the next NOT-YET-COMPLETE match's home input.
  // Used by the deferred cross-card focus advance — fired from `flushSave`
  // after a successful save when the card just transitioned from incomplete
  // to complete (see "Deferred cross-card focus advance" below and ADR 0001).
  // Walks forward through matchesToRender skipping any card whose pick is
  // already complete so the cursor lands on the next workable card, not the
  // next index.
  const focusNextIncompleteMatchHomeInput = useCallback(
    (currentMatchId: number) => {
      const orderedIds = matchesToRender
        .filter((m: any) => !!(m?.home?.id && m?.away?.id))
        .map((m: any) => m.id as number);
      const currentIdx = orderedIds.indexOf(currentMatchId);
      if (currentIdx === -1) return;
      for (let i = currentIdx + 1; i < orderedIds.length; i++) {
        const id = orderedIds[i]!;
        const candidate = picks[id];
        const candidateComplete =
          typeof candidate?.homeGoals === "number" &&
          typeof candidate?.awayGoals === "number";
        if (candidateComplete) continue;
        const target = document.querySelector<HTMLInputElement>(
          `input[data-match-home-input="${id}"]`
        );
        target?.focus();
        return;
      }
      // No incomplete card after this one — leave focus alone.
    },
    [matchesToRender, picks]
  );

  // Latest-ref of `focusNextIncompleteMatchHomeInput` so `flushSave` (which we
  // keep referentially stable via useCallback) can always invoke the freshest
  // version — one that closes over the current `picks` snapshot — without
  // having to list it as a dep and re-create flushSave on every keystroke.
  const focusNextIncompleteMatchHomeInputRef = useRef(focusNextIncompleteMatchHomeInput);
  focusNextIncompleteMatchHomeInputRef.current = focusNextIncompleteMatchHomeInput;

  // Perform the actual POST. Honors the latest pending payload for this match
  // so rapid edits collapse into a single trailing save (last-write-wins).
  const flushSave = useCallback(async (matchId: number) => {
    if (inFlightMatchIdsRef.current.has(matchId)) {
      // A save is already in flight; the latest payload is queued and will fire
      // when the current one resolves.
      return;
    }
    const payload = pendingPayloadsRef.current.get(matchId);
    if (!payload) return;

    const result = getPickResult(payload);
    if (!result) {
      pendingPayloadsRef.current.delete(matchId);
      return;
    }

    if (!normalizedUserId) {
      handleSignIn();
      return;
    }

    const match = matchesToRender.find((item: any) => item.id === matchId);
    if (!match?.date) {
      toast.error("Impossible d'enregistrer ce pronostic: date du match manquante.");
      pendingPayloadsRef.current.delete(matchId);
      return;
    }

    pendingPayloadsRef.current.delete(matchId);
    inFlightMatchIdsRef.current.add(matchId);
    setSavingMatchIds((prev) => {
      const next = new Set(prev);
      next.add(matchId);
      return next;
    });

    let succeeded = false;
    try {
      await savePrediction({
        competitionFeed: competitionConfig.competitionFeed,
        seasonId: competitionConfig.seasonId,
        matchId,
        result,
        booster: !!payload.booster,
        matchDeadline: match.date,
      });
      succeeded = true;
    } catch (error) {
      console.error("[flushSave] Failed to save prediction:", error);
      toast.error(error instanceof Error ? error.message : "Failed to save prediction");
    } finally {
      inFlightMatchIdsRef.current.delete(matchId);
      setSavingMatchIds((prev) => {
        const next = new Set(prev);
        next.delete(matchId);
        return next;
      });
    }

    if (succeeded) {
      // If no further edit landed while we were saving, mark as saved.
      if (!pendingPayloadsRef.current.has(matchId)) {
        setDirtyMatchIds((prev) => {
          if (!prev.has(matchId)) return prev;
          const next = new Set(prev);
          next.delete(matchId);
          return next;
        });
        setSavedMatchIds((prev) => {
          if (prev.has(matchId)) return prev;
          const next = new Set(prev);
          next.add(matchId);
          return next;
        });

        // Deferred cross-card focus advance (see ADR 0001 + "delay advance
        // until saved" follow-up). If this card's latest completion is still
        // pending an advance (i.e. the user transitioned it from incomplete
        // to complete and hasn't subsequently broken it), jump to the next
        // not-yet-complete card now that the server has acknowledged the
        // save. We defer to the next animation frame so React's saved-state
        // commit and the `useLayoutEffect` focus-restore pass run first;
        // otherwise the restore pass could yank focus back to this card
        // right after we move it.
        if (pendingAdvanceMatchIdsRef.current.has(matchId)) {
          pendingAdvanceMatchIdsRef.current.delete(matchId);
          requestAnimationFrame(() => {
            focusNextIncompleteMatchHomeInputRef.current(matchId);
          });
        }
      } else {
        // A newer edit is queued — fire it now (still pending visually).
        void flushSave(matchId);
      }
    }
  }, [matchesToRender, normalizedUserId, competitionConfig.competitionFeed, competitionConfig.seasonId, handleSignIn]);

  const handlePickChange = (
    matchId: number,
    val: PickValue,
    _meta?: { side?: "home" | "away" }
  ) => {
    // Captured BEFORE the setPicks update applies — picks[matchId] here is
    // the pre-keystroke pick. Used to detect "card just transitioned from
    // incomplete to complete" so we only auto-advance on completion (not on
    // edits to an already-complete card).
    const previousPick = picks[matchId];
    const wasComplete =
      typeof previousPick?.homeGoals === "number" &&
      typeof previousPick?.awayGoals === "number";
    setPicks((prev) => {
      const next = { ...prev };

      // If setting booster true on this match, ensure capacity
      if (val.booster) {
        let currentBoosters = Object.entries(next).filter(
          ([, v]) => v?.booster
        );
        // If this match is already boosted, nothing changes in capacity
        const alreadyBoosted = !!next[matchId]?.booster;

        if (!alreadyBoosted && currentBoosters.length >= boosterCapacity) {
          // Drop the oldest (or just the first) boosted pick to keep within capacity
          const toUnboost = Number(currentBoosters[0]![0]);
          next[toUnboost] = { ...next[toUnboost]!, booster: false };
        }

        // Clear booster on other matches if capacity is 1 and a different match is boosted
        if (boosterCapacity === 1) {
          Object.keys(next).forEach((idStr) => {
            const id = Number(idStr);
            if (id !== matchId && next[id]?.booster) {
              next[id] = { ...next[id]!, booster: false };
            }
          });
        }
      }

      next[matchId] = {
        winnerId: val.winnerId ?? null,
        outcome: val.outcome ?? null,
        round: val.round ?? null,
        booster: !!val.booster,
        homeGoals: val.homeGoals ?? null,
        awayGoals: val.awayGoals ?? null,
      };

      return next;
    });

    const bothFilled =
      typeof val.homeGoals === "number" && typeof val.awayGoals === "number";

    if (!bothFilled) {
      // Cancel any pending save and drop dirty/saved status — incomplete pick.
      const existing = debounceTimersRef.current.get(matchId);
      if (existing) {
        clearTimeout(existing);
        debounceTimersRef.current.delete(matchId);
      }
      pendingPayloadsRef.current.delete(matchId);
      // Card is no longer complete — drop any deferred advance that was
      // earned by a previous completion. If the user re-completes the card,
      // a fresh advance will be queued by the `justCompleted` branch below.
      pendingAdvanceMatchIdsRef.current.delete(matchId);
      setDirtyMatchIds((prev) => {
        if (!prev.has(matchId)) return prev;
        const next = new Set(prev);
        next.delete(matchId);
        return next;
      });
      setSavedMatchIds((prev) => {
        if (!prev.has(matchId)) return prev;
        const next = new Set(prev);
        next.delete(matchId);
        return next;
      });
      return;
    }

    pendingPayloadsRef.current.set(matchId, val);
    setDirtyMatchIds((prev) => {
      if (prev.has(matchId)) return prev;
      const next = new Set(prev);
      next.add(matchId);
      return next;
    });
    setSavedMatchIds((prev) => {
      if (!prev.has(matchId)) return prev;
      const next = new Set(prev);
      next.delete(matchId);
      return next;
    });

    // Deferred cross-card focus advance (see ADR 0001 + "delay advance until
    // saved" follow-up). When a keystroke transitions the card from
    // incomplete to complete, we no longer move focus immediately — instead
    // we just remember that this card has "earned" an advance, and the
    // actual focus jump happens from `flushSave`'s success branch once the
    // server has acknowledged the save. This keeps the user's caret on the
    // card they just finished until that card's pick is durably saved, so
    // they can correct it without losing the input focus to a different
    // card. If the user clears a digit before the save fires, the
    // `!bothFilled` branch above drops the advance flag so we don't jump
    // after the (now stale) save would otherwise resolve.
    const justCompleted = !wasComplete && bothFilled;
    if (justCompleted) {
      pendingAdvanceMatchIdsRef.current.add(matchId);
    }

    const existing = debounceTimersRef.current.get(matchId);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => {
      debounceTimersRef.current.delete(matchId);
      void flushSave(matchId);
    }, SAVE_DEBOUNCE_MS);
    debounceTimersRef.current.set(matchId, timer);
  };

  // Latest-ref of `handlePickChange` so the stable per-match callbacks below
  // always invoke the freshest closure (which captures the current `picks`,
  // `boosterCapacity`, etc.) without changing their own identity. This is the
  // "useEvent polyfill" pattern. The ref is reassigned each render — safe
  // because it's only read inside async event handlers, never during render.
  const handlePickChangeRef = useRef(handlePickChange);
  handlePickChangeRef.current = handlePickChange;

  // Cache per-match bound `onPickChange` callbacks so that MatchCard's
  // `onPickChange` prop keeps a stable reference across renders. Combined with
  // `React.memo`, this lets the renderer skip MatchCards whose match/pick/odds
  // haven't changed — fixing the "all cards re-render on every keystroke" issue
  // observed on the Predictions page.
  const pickChangeByMatchIdRef = useRef(
    new Map<number, (val: PickValue, meta?: { side?: "home" | "away" }) => void>()
  );
  const getPickChangeForMatch = useCallback((matchId: number) => {
    const map = pickChangeByMatchIdRef.current;
    let cb = map.get(matchId);
    if (!cb) {
      cb = (val: PickValue, meta?: { side?: "home" | "away" }) => {
        handlePickChangeRef.current(matchId, val, meta);
      };
      map.set(matchId, cb);
    }
    return cb;
  }, []);

  // UI booster info helper
  const boostersLeft = Math.max(boosterCapacity - boostersSelected, 0);
  // console.log('boostersLeft', boostersLeft);
  // console.log('boostersSelected', boostersSelected);
  // console.log('boosterCapacity', boosterCapacity);
  // console.log('boosterUsedOnPastMatch', boosterUsedOnPastMatch);
  // console.log('pastMatches', pastMatches);

  // Derive streak state for the Friday→Thursday window from dated matches and picks.
  const { streakDays, streakStatus } = useMemo(() => {
    const now = moment();
    const matchesWithDates: StreakMatchMeta[] = [];
    weekMatches.forEach((match: any) => {
      if (!match?.date) {
        return;
      }

      const dateMoment = getMatchLocalMoment(match.date);
      if (!dateMoment?.isValid()) {
        return;
      }

      const pick = picks[match.id];
      const hasPick =
        typeof pick?.homeGoals === "number" && typeof pick?.awayGoals === "number";

      matchesWithDates.push({
        matchId: match.id,
        dateMoment,
        hasPick,
      });
    });

    if (matchesWithDates.length === 0) {
      return {
        streakDays: buildDefaultStreakDays(),
        streakStatus: "ongoing" as "completed" | "broken" | "ongoing",
      };
    }

    let referenceDate =
      matchesWithDates[0]?.dateMoment?.clone() ?? now.clone();

    matchesWithDates.forEach((entry: StreakMatchMeta) => {
      if (entry.dateMoment.isBefore(referenceDate)) {
        referenceDate = entry.dateMoment.clone();
      }
    });

    const referenceIsoWeekday = referenceDate.isoWeekday();
    const startOfWeek = referenceDate.clone().isoWeekday(5);
    if (referenceIsoWeekday < 5) {
      startOfWeek.subtract(7, "days");
    }

    const dayEntries = STREAK_DAY_LABELS.map((label, idx) => {
      const dayMoment = startOfWeek.clone().add(idx, "days");
      const dayMatches = matchesWithDates.filter((entry: StreakMatchMeta) =>
        entry.dateMoment.isSame(dayMoment, "day")
      );

      const matchesThatStarted = dayMatches.filter(
        (entry: StreakMatchMeta) => entry.dateMoment.isSameOrBefore(now)
      );

      const isPastDay = dayMoment.clone().endOf("day").isBefore(now);
      const dayEvaluated = isPastDay || matchesThatStarted.length > 0;

      let status: StreakDayStatus = "empty";
      if (!dayEvaluated) {
        status = "empty";
      } else if (dayMatches.length === 0) {
        status = "completed";
      } else {
        const matchesToCheck = isPastDay ? dayMatches : matchesThatStarted;
        const allPicksMade =
          matchesToCheck.length > 0 &&
          matchesToCheck.every((entry) => entry.hasPick);

        status = allPicksMade ? "completed" : "broken";
      }

      return {
        day: label,
        status,
        evaluated: dayEvaluated,
      };
    });

    const hasBrokenDay = dayEntries.some((entry) => entry.status === "broken");
    const allEvaluated = dayEntries.every((entry) => entry.evaluated);
    const allEvaluatedCompleted = dayEntries
      .filter((entry) => entry.evaluated)
      .every((entry) => entry.status === "completed");

    let computedStatus: "completed" | "broken" | "ongoing" = "ongoing";
    if (hasBrokenDay) {
      computedStatus = "broken";
    } else if (allEvaluated && allEvaluatedCompleted) {
      computedStatus = "completed";
    }

    return {
      streakDays: dayEntries.map(({ day, status }) => ({ day, status })),
      streakStatus: computedStatus,
    };
  }, [weekMatches, picks]);

  const activePlayWeekId = useMemo(
    () => getActivePlayWeekIdFromMatches(competitionData?.matches ?? [], moment()),
    [competitionData?.matches]
  );

  const isFutureGameweekView = useMemo(
    () => isFutureGameweek(competitionData?.weeks ?? [], weekId, activePlayWeekId),
    [competitionData?.weeks, weekId, activePlayWeekId]
  );

  const weekMatchDateRangeLabel = useMemo(
    () => formatMatchWeekDateRange(weekMatches, locale),
    [weekMatches, locale]
  );

  const futureWeekSectionHeading =
    isFutureGameweekView && weekMatchDateRangeLabel ? weekMatchDateRangeLabel : null;

  if (isLoadingPredictions) {
    return (
      <PredictionsPageSkeleton
        weekSwitch={<WeekSwitch competitionData={competitionData} />}
        showPointsOverview={isAuthenticated && firstMatchOfFirstMatchdayHasStarted}
      />
    );
  }

  const renderPredictionMatch = (match: any, keyPrefix: string) => {
    const hasSides = !!(match?.home?.id && match?.away?.id);
    const current: PickValue = picks[match.id] ?? EMPTY_PICK;

    if (!hasSides) {
      return (
        <Fragment key={`${keyPrefix}-${match.id}`}>
          <Card className="rounded-2xl border border-gray-200 bg-white">
            <CardContent className="p-5 md:p-6">
              <div className="text-sm text-amber-700">{t("predictions.matchIncomplete")}</div>
            </CardContent>
          </Card>
        </Fragment>
      );
    }

    const matchIsSaving = savingMatchIds.has(match.id);
    const matchIsDirty = dirtyMatchIds.has(match.id);
    const matchIsSaved = savedMatchIds.has(match.id);
    const saveStatus: "idle" | "pending" | "saved" =
      matchIsDirty || matchIsSaving ? "pending" : matchIsSaved ? "saved" : "idle";

    return (
      <Fragment key={`${keyPrefix}-${match.id}`}>
        <MatchCard
          match={match}
          pick={current}
          seasonId={competitionConfig.seasonId}
          onPickChange={getPickChangeForMatch(match.id)}
          // We no longer disable inputs while saving — user can keep editing and
          // the latest payload wins (handled by debounce + in-flight queue).
          disabled={false}
          isSaving={matchIsSaving}
          saveStatus={saveStatus}
          odds={oddsByMatchId.get(match.id) ?? EMPTY_ODDS}
          isFavorite={false}
          onAuthRequired={!isAuthenticated ? handleSignIn : undefined}
          boosterDisabled={!current.booster && boostersSelected >= boosterCapacity}
          boosterAlreadyUsed={boosterUsedOnPastMatch && boostersSelected >= boosterCapacity}
        />
      </Fragment>
    );
  };

  return (
    <div className="min-h-screen bg-[var(--rtbf-dark,#001425)] pb-20 pt-6 px-3 sm:px-4">
      <div className="relative overflow-hidden rounded-2xl mx-auto max-w-6xl bg-black">
        <WeekSwitch competitionData={competitionData} />
      </div>
      {isAuthenticated && firstMatchOfFirstMatchdayHasStarted && (
      <div className="mx-auto mt-4 max-w-6xl">
        <PointsOverviewCard
          headlineWeekScore={userRanksData?.weekRank?.score}
          dailyRank={userRanksData?.weekRank?.rank}
          totalPoints={userRanksData?.generalRank?.score}
          overallRank={userRanksData?.generalRank?.rank}
          isLoading={isLoadingUserRanks}
        />
      </div>
      )}

      {/* <div className="max-w-6xl mx-auto"> */}
      <div className="mx-auto mt-4 md:mt-6 grid max-w-6xl grid-cols-1 gap-4 md:gap-6">
        {showPredictionProgress && (
        <PredictionProgressCard
          remaining={remainingPredictionsCount}
          total={matchesRequiringPredictionCount}
          doneLabel={t("predictions.progress.done")}
          leftLabel={(remaining) =>
            remaining === 1
              ? t("predictions.progress.leftSingle")
              : t("predictions.progress.leftMultiple").replace("{count}", String(remaining))
          }
        />
        )}
        {!isAuthenticated && (
          <div
            className="grid max-h-[min(280px,50vh)] min-h-0 w-full grid-cols-1 overflow-hidden rounded-[8px] bg-gradient-to-b from-[var(--rtbf-blue,#0076A9)] to-[var(--rtbf-blue-dark,#003E63)] shadow-[0px_2px_8px_rgba(0,0,0,0.12)] sm:max-h-[240px] sm:grid-cols-2"
            data-name="No auth required card"
          >
            <div className="flex min-h-0 min-w-0 flex-col justify-center gap-5 p-4 sm:px-6 sm:py-4 sm:pr-4">
              <div className="flex w-full flex-col gap-2.5 text-white">
                <p className="text-[22px] font-semibold leading-snug sm:text-2xl">
                  {t("predictions.signUpCardTitle")}
                </p>
                <p className="text-sm font-normal leading-snug text-white/90">
                  {t("predictions.signUpCardBody")}
                </p>
              </div>
              <button
                type="button"
                onClick={handleSignIn}
                className="inline-flex min-h-[44px] w-auto shrink-0 items-center justify-center self-start rounded-full bg-[var(--rtbf-orange,#fdc300)] px-5 py-2.5 text-sm font-bold text-[var(--rtbf-blue-dark,#003e63)] shadow-sm transition-colors hover:bg-[#e6b200] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FDC300] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--rtbf-blue-dark,#003E63)]"
              >
                {t("auth.signIn")}
              </button>
            </div>
            <div
              className="relative hidden min-h-0 sm:flex sm:h-full sm:w-full sm:items-center sm:justify-center sm:py-3 sm:pr-4 sm:pl-0"
              aria-hidden
            >
              <Image
                src={PREDICTIONS_LOGIN_HERO_SRC}
                alt=""
                width={640}
                height={360}
                className="h-full max-h-[220px] w-full object-cover object-center sm:rounded-sm"
                sizes="(max-width: 639px) 0px, (max-width: 1280px) 45vw, 520px"
              />
            </div>
          </div>
        )}

        {showSummaryWidgets && isAuthenticated && <PointsCard
          className="mt-6"
          isLoading={isLoadingUserRanks}
          weekRank={
            userRanksData?.weekRank
              ? {
                  rank: userRanksData.weekRank.rank,
                  score: userRanksData.weekRank.score,
                }
              : null
          }
          overallRank={
            userRanksData?.generalRank
              ? {
                  rank: userRanksData.generalRank.rank,
                  score: userRanksData.generalRank.score,
                }
              : null
          }
        />}

        {/* Keep Your Streak Card */}
        {/* <div className="w-full flex h-full"> */}
        {showSummaryWidgets && isAuthenticated && <Streak 
          className="mt-2 md:mt-6"
          streak={streakStatus}
          days={streakDays}
        />}
        {/* </div> */}

        {/* <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-gray-700">
            Booster capacity this week:{" "}
            <span className="font-semibold">
              {boosterCapacity} (prev week picks: {prevWeekPredCount})
            </span>
          </div>
          <Badge variant="secondary" className="text-xs">
            Boosters left: {boostersLeft}
          </Badge>
        </div> */}
        {/* Match sections: future week = single title (date range); else next up / coming / finished (prono-dpg-ui) */}
        {futureWeekSectionHeading &&
        (nextUpFlatMatches.length > 0 ||
          remainingFutureMatches.length > 0 ||
          remainingFinishedMatches.length > 0) ? (
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 md:gap-6">
            <div className="col-span-full">
              <h2 className="text-lg font-bold text-white">{futureWeekSectionHeading}</h2>
            </div>
            {nextUpFlatMatches.map((match: any) => renderPredictionMatch(match, "next"))}
            {nextUpFlatMatches.length > 0 && isAuthenticated ? (
              <LeaguePromoCard
                isAuthenticated={isAuthenticated}
                onAuthRequired={handleSignIn}
              />
            ) : null}
            {remainingFutureMatches.map((match: any) => renderPredictionMatch(match, "fut"))}
            {remainingFinishedMatches.map((match: any) => renderPredictionMatch(match, "fin"))}
          </div>
        ) : (
        <>
          {nextUpFlatMatches.length > 0 && (
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 md:gap-6">
              <div className="col-span-full">
                <h2 className="text-lg font-bold text-white">{t("predictions.nextUp")}</h2>
              </div>
              {nextUpFlatMatches.map((match: any) => renderPredictionMatch(match, "next"))}
              {isAuthenticated ? (
                <LeaguePromoCard
                  isAuthenticated={isAuthenticated}
                  onAuthRequired={handleSignIn}
                />
              ) : null}
            </div>
          )}

          {remainingFutureMatches.length > 0 && (
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 md:gap-6">
              <div className="col-span-full">
                <h2 className="text-lg font-bold text-white">{t("predictions.comingDays")}</h2>
              </div>
              {remainingFutureMatches.map((match: any) => renderPredictionMatch(match, "fut"))}
            </div>
          )}

          {remainingFinishedMatches.length > 0 && (
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 md:gap-6">
              <div className="col-span-full">
                <h2 className="text-lg font-bold text-white">{t("predictions.finishedMatches")}</h2>
              </div>
              {remainingFinishedMatches.map((match: any) => renderPredictionMatch(match, "fin"))}
            </div>
          )}
        </>
        )}

          {matchesToRender.length === 0 && (
            <div className="col-span-full rounded-2xl border border-dashed border-gray-300 bg-white p-12 text-center">
              <div className="text-lg font-semibold">
                {t('predictions.noMatches')}
              </div>
              <div className="text-sm text-gray-500 mt-1">{t('predictions.checkBack')}</div>
            </div>
          )}

          {showPrizesWidget && hasWeeklyPrizeForCompetitionWeek(weekId) && (
            <div className="col-span-full">
              <div className="w-full md:w-[calc((100%-1.5rem)/2)]">
                <WeekPrizeWidget
                  weekId={weekId}
                  scheduleWeeks={competitionData?.weeks}
                />
              </div>
            </div>
          )}
        </div>

      </div>
  );
};

export default Predictions;
